import { message } from "antd";
import FileSaver from "file-saver";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import api from "../services/api";
import { isAntesTerceiroDiaUtil, isMobile } from "../utils";
import { yearConfigPL4173 } from "../constants/others/exterior";

export type CalcStatus =
  | "Integração inativa"
  | "Imposto apurado"
  | "Imposto devido";

interface GetClientsParams {
  page?: number;
  pageSize?: number;
  searchText?: string;
  planActive?: any;
  resp?: string[];
  statusBolsa?: CalcStatus[];
  statusExterior?: CalcStatus[];
  getAllDarfStatus?: boolean;
  orderBy?: string;
  orderDirection?: string;
  month?: number[];
  year?: number[];
  type?: string[];
  impostoDevido?: string;
  darfEnviado?: string;
  darfPago?: string;
  updateAllClients?: boolean;
  processAllClients?: boolean;
  fromClients?: boolean
  selectClient?: string
  justDownload?: boolean
}

interface IClientsContext {
  clients: IClient[];
  setClients: any;
  darfs: IClient[];
  setDarfs: any;
  professionals: IProfessional[];
  getClients: (params?: GetClientsParams) => Promise<any>;
  getProventos: (
    cpf: string,
    year?: number,
    type?: string,
    onFinish?: (data: any) => void
  ) => Promise<any>;
  getPatrimonios: (
    cpf: string,
    type?: string,
    onFinish?: (data: any) => void
  ) => Promise<any>;
  getNotifications: (cpf: string) => void;
  totalClients: number;
  totalDarfs: number;
  totalPatrimonio: number;
  totalPatrimonioReverse: number;
  getProfessionals: (
    page?: number,
    value?: string,
    all?: boolean
  ) => Promise<void>;
  totalProfessionals: number;
  typeProventos: string;
  setTypeProventos: any;
  searchClients: (search: string) => IClient[];
  searchProfessionals: (search: string) => IProfessional[];
  loginInClient: (client: IClient) => void;
  getClientToken: (client: IClient) => Promise<string>;
  removeClient: (page: number) => Promise<void>;
  removeProfessional: (data: any) => Promise<void>;
  selectedClient: IClient | null;
  proventosLoading: any;
  patrimoniosLoading: any;
  selectedProfessional: IProfessional | null;
  setSelectedClient: Dispatch<SetStateAction<IClient | null>>;
  setProfessionalId: (id: string | null) => void;
  yearProventos: any;
  setSelectedProfessional: (professional: IProfessional | null) => void;
  newClient: IClient | null;
  createStep: number;
  verifyClientVelotax: (client: IClient) => Promise<void>;
  createClient: (client: IClient, page?: number) => Promise<void>;
  updateClient: (
    client: IClient,
    page?: number,
    filters?: any
  ) => Promise<void>;
  updateProfessional: (client: IClient) => Promise<void>;
  setCreateStep: (step: number) => void;
  setNewClient: (client: any) => void;
  setYearProventos: any;
  loadingGet: boolean;
  proventos: any;
  patrimonios: any;
  notificacoes: INotificacoes[];
  loadingNotificacoes: boolean;
  setNotificacoes: React.Dispatch<SetStateAction<INotificacoes[]>>;
  professionalGet: boolean;
  setProventosLoading: any;
  loadingLogin: boolean;
  setLoadingGet: any;
  handleDownloadSheet: any;
  createClientDeleted: ClientDeleted;
  setCreateClientDeleted: React.Dispatch<React.SetStateAction<ClientDeleted>>;
  restoreClient: (username: string) => Promise<void>;
  proventosFileUrl: string;
  patrimoniosFileUrl: string;
  getClientsUpdating: () => Promise<void>
}

export interface IClient {
  resp: any;
  _id: string;
  name: string;
  fullname?: string;
  email: string;
  phone?: string;
  username: string;
  type?: string;
  manager: string;
  manager_id: string;
  userPlanInfoVelotax: IPlanInfo;
  plan: any;
  planActive: boolean;
  clientPlanActive: boolean;
  hasPlanXp: boolean;
  planDueDate: number;
  planType: string;
  planName?: string;
  planCreatedAt?: Date;
  userId: string;
  month?: number;
  year?: number;
  id?: string;
  statusBolsa?: string;
  urlDarfBolsa?: string;
  statusExterior?: string;
  urlDarfExterior?: string;
  idDarfBolsa?: string;
  idDarfExterior?: string;
  impostoDevido?: number;
  cancelRecuring?: boolean;
  planCancelPending?: boolean;
  isConcierge?: boolean;
  wallet?: any;
  code?: Array<any>;
  closeIntegrationBolsa?: boolean;
  closeIntegrationExterior?: boolean;
  notifications?: any[];
  updatingB3Integration?: boolean;
  b3IntegrationUpdated?: boolean;
  integration?: string;
 
}

export interface IPlanInfo {
  active: boolean;
  dueDate: number;
  type: string;
  paid: boolean;
  actionFree: boolean;
}

export interface IProfessional {
  _id: string;
  name: string;
  fullname?: string;
  email: string;
  username: string;
  office: boolean;
  acesso: string;
}

export interface ClientDeleted {
  showModal?: boolean;
  name?: string;
  cpf?: string;
}

export interface INotificacoes {
  message: string;
  date: Date;
  viewed: boolean;
  viewedDate?: Date;
}

export const ClientsContext = createContext<IClientsContext>(
  {} as IClientsContext
);

export const ClientsProvider: React.FC = ({ children }) => {
  const [clients, setClients] = useState<IClient[]>([]);
  const [escritorio, setEscritorio] = useState<any>(null);
  const [integration, setIntegration] = useState<string[]>([]);
  const [typeProventos, setTypeProventos] = useState<string>("");
  const [yearProventos, setYearProventos] = useState<number>(
    new Date().getFullYear()
  );
  const [proventos, setProventos] = useState<any[]>([]);
  const [patrimonios, setPatrimonios] = useState<any[]>([]);
  const [notificacoes, setNotificacoes] = useState<INotificacoes[]>([]);
  const [loadingNotificacoes, setLoadingNotificacoes] = useState(false);
  const [proventosLoading, setProventosLoading] = useState<Boolean>(false);
  const [patrimoniosLoading, setPatrimoniosLoading] = useState<Boolean>(false);
  const [totalClients, setTotalClients] = useState<number>(0);
  const [totalPatrimonio, setTotalPatrimonio] = useState<number>(0);
  const [totalPatrimonioReverse, setTotalPatrimonioReverse] = useState<number>(0);
  const [darfs, setDarfs] = useState<IClient[]>([]);
  const [totalDarfs, setTotalDarfs] = useState<number>(0);
  const [proventosFileUrl, setProventosFileUrl] = useState("");
  const [patrimoniosFileUrl, setPatrimoniosFileUrl] = useState("");
  const [totalProfessionals, setTotalProfessionals] = useState<number>(0);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [professionalId, setProfessionalId] = useState<string | null>(null);
  const [selectedProfessional, setSelectedProfessional] =
    useState<IProfessional | null>(null);
  const [newClient, setNewClient] = useState<IClient | null>(null);
  const [createStep, setCreateStep] = useState(1);
  const [loadingGet, setLoadingGet] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [professionalGet, setProfessionalGet] = useState(false);
  const [professionalSheetBuffer, setProfessionalSheetBuffer] = useState([]);
  const [buffer, setBuffer] = useState([]);
  const [createClientDeleted, setCreateClientDeleted] = useState<{
    showModal?: boolean;
    name?: string;
    cpf?: string;
  }>({});

  const createClient = async (data: any, currentPage?: number) => {
    setLoadingGet(true);
    const professional = !!data.professional;

    api
      .post(professional ? "/assessores/" : "/clients/register", {
        cpf: data.username.replace(/[_.-/-]/g, ""),
        name: data.name,
        id: `pro-velotax-${Math.random()}`,
        mail: data.email,
        phone: data.phone ?? "",
        acesso: data?.acesso ?? "",
        terms: true,
        professional,
        from: "PRO",
        plan: data.plan,
        assessores: data?.assessores,
      })
      .then((response) => {
        const person = professional ? "Profissional" : "Cliente";

        if (response?.data?.userDeleted) {
          setLoadingGet(false);
          setCreateClientDeleted({
            showModal: true,
            name: data.name,
            cpf: data.username,
          });
        } else {
          response.data.code === "emailSended"
            ? message.success(response.data.message)
            : message.success(`${person} ${data.name} cadastrado com sucesso`);

          professional
            ? getProfessionals()
            : getClients({ page: currentPage ?? 1 });
        }
      })
      .catch((err) => {
        if (
          err?.response?.status === 400 ||
          err?.response?.status === 409 ||
          err?.response?.status === 500
        )
          return message.error(
            err.response.data.message ||
              `Erro ao criar ${professional ? "profissional" : "cliente"}`
          );

        return message.error(
          `Erro ao criar ${professional ? "profissional" : "cliente"}`
        );
      });
  };
  const updateClient = async (data: any, page?: number, filters: any = {}) => {
    const professional = !!data.professional;
    let payload: any = {
      cpf: data.username.replace(/[_.-/-]/g, ""),
      name: data.name,
      id: `pro-velotax-${Math.random()}`,
      mail: data.email,
      phone: data.phone,
      terms: true,
      professional,
      from: "PRO",
      plan: data.plan,
      assessores: data?.assessores,
      tableEdit: data?.tableEdit,
      userId: data?.userId,
    };

    api
      .put(professional ? "/assessores/" : "/clients/update", {
        ...payload,
      })
      .then((response) => {
        const person = professional ? "Profissional" : "Cliente";

        response.data.code === "emailSended"
          ? message.success(response.data.message)
          : message.success(`${person} ${data.name} editado com sucesso`);

        setLoadingGet(true);

        professional ? getProfessionals() : getClients({ ...filters, page });
      })
      .catch((err) => {
        if (err?.response?.status === 400 || err?.response?.status === 409)
          return message.error(
            err.response.data.message ||
              `Erro ao atualizar ${professional ? "profissional" : "cliente"}`
          );

        return message.error(
          `Erro ao atualizar ${professional ? "profissional" : "cliente"}`
        );
      });
  };
  const updateProfessional = async (data: any) => {
    api
      .put("/assessores/update", {
        cpf: data.username.replace(/[_.-/-]/g, ""),
        name: data.name,
        id: `pro-velotax-${Math.random()}`,
        mail: data.email,
        acesso: data.acesso,
        terms: true,
        from: "PRO",
      })
      .then((response) => {
        response.data.code === "emailSended"
          ? message.success(response.data.message)
          : message.success(`Assessor atualizado com sucesso`);

        setLoadingGet(true);

        getProfessionals();
      })
      .catch((err) => {
        if (err?.response?.status === 400 || err?.response?.status === 409)
          return message.error(
            err.response.data.message || "Erro ao atualizar profissional"
          );

        return message.error(`Erro ao criar profissional`);
      });
  };

  const getProventos = async (
    cpf: string,
    year?: number,
    type?: string,
    onFinish?: (data: any) => void
  ) => {
    if (cpf) {
      setProventosLoading(true);
      const { data } = await api.get(`/clients/get-proventos/${cpf}`, {
        params: {
          year,
          type,
        },
      });

      setProventosFileUrl(data.fileUrl);
      setTypeProventos("provento");
      setProventos(data?.proventos || []);
      if (data?.updated) {
        getNotifications(cpf);
      }
      // setNotificacoes(data.notificacoes);
      setProventosLoading(false);
      if (onFinish) onFinish(data);
    }
  };

  const getPatrimonios = async (
    cpf: string,
    type?: string,
    onFinish?: (data: any) => void
  ) => {
    if (cpf) {
      setPatrimoniosLoading(true);
      const { data } = await api.get(`/clients/get-patrimonios/${cpf}`, {
        params: {
          type,
        },
      });

      setPatrimoniosFileUrl(data.fileUrl);
      setPatrimonios(data?.patrimonios || []);
      setPatrimoniosLoading(false);
      setSelectedClient((client) => ({
        ...client!,
        wallet: {
          ...client?.wallet,
          posicoes: data?.patrimonios || [],
        },
      }));
      if (onFinish) onFinish(data);
    }
  };

  const getNotifications = async (cpf: string) => {
    setLoadingNotificacoes(true);
    const { data } = await api.get(`/clients/notification/${cpf}`);
    setNotificacoes(data);
    setLoadingNotificacoes(false);
  };

  const verifyClientVelotax = async (client: IClient) => {
    const { data } = await api.get<IClient>(
      `/clients/clientInfo/${client.username.replace(/[_.-/-]/g, "")}`
    );
    setCreateStep(3);
    if (data) {
      setNewClient({ ...data, name: data?.fullname || "" });
    }
  };

  const getClientsUpdating = useCallback(async () => {
    const cpfs: string[] = []
    clients.forEach(c => {
      if(c.updatingB3Integration) cpfs.push(c.username)
    })
    const { data } = await api.post('/clients/integration-updating', {
      clients: cpfs
    })
    
    const newClients = clients.map(c => {
      const stillUpdating = data.find((cpf: string) => cpf === c.username)
      if(c.updatingB3Integration && !stillUpdating) {
        if(c.username === selectedClient?.username) {
          setSelectedClient({...selectedClient, b3IntegrationUpdated: true, updatingB3Integration: false})
        }
        return {...c, b3IntegrationUpdated: true, updatingB3Integration: false}
      } else return c
    })
    
    setClients(newClients)
  }, [clients, selectedClient])

  const removeCurrent = useCallback((data: any)=> {
    const currentMonth = new Date().getMonth();
    const newData = data.filter((darf: any)=> {
      if (darf?.year == yearConfigPL4173 && darf?.month == currentMonth && isAntesTerceiroDiaUtil()) return;
      else return darf;
    })
  return newData;
  }, [])

  const downloadSheetClientsFromPulling = async (params: any) => {
    const delayPulling = 10000;
    let buffer: any;
    for(let i = 0 ; i < 200 ; i++) {
          const response = await api.get(
            `/clients/sheet`,
            { params }
          );
          await new Promise((res, rej) => {
            setTimeout(() => {
              res(true)
            }, delayPulling)
          })
          if (response?.data?.status === "DONE") {
            buffer = response?.data?.buffer?.data;
            i = 200;
          }
        }
        setLoadingGet(false);
        if (!buffer) {
          message.error("Algo deu errado ao gerar a planilha")
          return
        }
        const data = Uint8Array.from(buffer);
        const blob = new Blob([data], { type: "attach.contentType" });
        FileSaver.saveAs(blob, `clientes.xlsx`);
  }

  const getClients = useCallback(async (params?: GetClientsParams) => {
    const {
      page,
      pageSize,
      searchText,
      planActive,
      resp,
      type,
      darfEnviado,
      darfPago,
      getAllDarfStatus,
      orderBy,
      orderDirection,
      month,
      year,
      statusBolsa,
      statusExterior,
      updateAllClients,
      processAllClients,
      fromClients,
      selectClient,
      justDownload
    } = params ?? {};
    setLoadingGet(true);
    try {
      const params = {
        page: page ?? 1,
        pageSize: pageSize ?? 10,
        planActive,
        resp,
        statusBolsa,
        statusExterior,
        searchText,
        impostoDevido: "Impostos devidos",
        darfEnviado,
        darfPago,
        orderBy,
        orderDirection,
        month,
        year,
        type,
        updateAllClients,
        processAllClients,
        fromClients,
        justDownload
      };
      if (justDownload) {
        downloadSheetClientsFromPulling(params)
        return
      }
      const response = await api.get(
        `/clients${getAllDarfStatus ? "/allDarf" : ""}`,
        { params }
      );
      if (updateAllClients) {
        message.info(`Atualizando os patrimônios de todos os seus clientes`, 5);
      } else {
        if (getAllDarfStatus) {
          const data = removeCurrent(response?.data?.data);
          setDarfs(data);
          setTotalDarfs(response?.data?.total);
          setIntegration(response?.data?.integration || []);
        } else {
          setClients(response?.data?.data);
          setTotalClients(response?.data?.total);
          setTotalPatrimonio(response?.data?.sumPatrimonio);
          setTotalPatrimonioReverse(response?.data?.sumPatrimonioReverse);
          setEscritorio(response?.data?.escritorio);
          if(selectClient) {
            const cliente = (response?.data?.data as IClient[] || []).find((c: IClient) => c.username === selectClient)
            if(cliente) setSelectedClient(cliente)
          }
        }
        setBuffer(response?.data?.buffer?.data);
      }
      setLoadingGet(false);
      return {
        clients: response?.data?.data,
        buffer: response?.data?.buffer?.data,
      };
    } catch (err: any) {
      setLoadingGet(false);
      message.error(err?.response?.data?.message || "Algo deu errado");
    }
  }, []);

  const handleDownloadSheet = async (from: string, filters?: any) => {
    try {
      let localBuffer: any;
      if (from === "Clientes") {
        await getClients({ processAllClients: true, justDownload: true });
        return;
      }
      if (from === "DARFs") {
        const resp = await getClients({
          processAllClients: true,
          getAllDarfStatus: true,
          month: filters?.month,
          year: filters?.year,
        });
        localBuffer = resp?.buffer || undefined;
      }
      const data = Uint8Array.from(
        from === "Assessores" ? professionalSheetBuffer : localBuffer ?? buffer
      );
      const blob = new Blob([data], { type: "attach.contentType" });
      FileSaver.saveAs(blob, `${from}.xlsx`);
    } catch (error: any) {
      message.error(error);
    }
  };

  const getProfessionals = useCallback(
    async (page?: number, value?: string, all?: boolean) => {
      setProfessionalGet(true);
      const response = await api.get("/assessores", {
        params: {
          page,
          value,
          all,
        },
      });
      setProfessionalSheetBuffer(response.data?.buffer?.data);
      setProfessionals(response.data.assessores);
      setTotalProfessionals(response.data.count);
      setProfessionalGet(false);
    },
    []
  );

  const searchClients = (search: string) => {
    const searchText = search.toLocaleLowerCase();
    return clients.filter(
      (client) =>
        client.email?.toLocaleLowerCase()?.includes(searchText) ||
        client.name?.toLocaleLowerCase()?.includes(searchText) ||
        client.username?.replace(/[.-]/g, "")?.includes(searchText)
    );
  };

  const searchProfessionals = (search: string) => {
    const searchText = search.toLocaleLowerCase();
    return professionals.filter(
      (client) =>
        client.email?.toLocaleLowerCase()?.includes(searchText) ||
        client.name?.toLocaleLowerCase()?.includes(searchText) ||
        client.username?.replace(/[.-]/g, "")?.includes(searchText)
    );
  };

  const getClientToken = async (client: IClient) => {
    const { data } = await api.post(`/clients/login/${client.username}`);
    return data.token;
  };

  const loginInClient = async (client: IClient) => {
    try {
      setLoadingLogin(true);
      const token = await getClientToken(client);
      setLoadingLogin(false);
      window.open(
        `${process.env.REACT_APP_VELOTAX_URL}/p/sso/${token}`,
        isMobile() ? "_self" : "_blank"
      );
    } catch (err) {
      setLoadingLogin(false);
    }
  };

  const removeClient = async (page: number) => {
    await api.delete(`/clients/${selectedClient?._id}`, {
      data: { professionalId },
    });
    getClients({ page });
  };

  const removeProfessional = async (data: any) => {
    await api.delete(`/assessores/delete`, { data: { cpf: data.username } });
    getProfessionals();
  };

  const restoreClient = async (username: string) => {
    try {
      setLoadingGet(true);
      const { data } = await api.post(`/clients/restore`, { username });
      if (data?.code === 403) message.error(data?.message);
      else {
        message.success(data.message);
        getClients();
      }
    } catch (error: any) {
      message.error(
        "Não foi possível recuperar informações do cliente. Tente novamente mais tarde"
      );
    }
  };

  return (
    <ClientsContext.Provider
      value={{
        loadingLogin,
        proventosLoading,
        patrimoniosLoading,
        proventos,
        patrimonios,
        loadingGet,
        setTypeProventos,
        typeProventos,
        setYearProventos,
        setProventosLoading,
        clients,
        getProventos,
        getPatrimonios,
        getNotifications,
        notificacoes,
        loadingNotificacoes,
        setNotificacoes,
        setClients,
        darfs,
        setDarfs,
        yearProventos,
        totalClients,
        totalDarfs,
        totalProfessionals,
        totalPatrimonio,
        totalPatrimonioReverse,
        professionals,
        getClients,
        getProfessionals,
        searchClients,
        searchProfessionals,
        loginInClient,
        removeClient,
        removeProfessional,
        selectedClient,
        selectedProfessional,
        setSelectedProfessional,
        setSelectedClient,
        setProfessionalId,
        verifyClientVelotax,
        createStep,
        newClient,
        createClient,
        setCreateStep,
        setNewClient,
        getClientToken,
        setLoadingGet,
        updateClient,
        updateProfessional,
        professionalGet,
        handleDownloadSheet,
        createClientDeleted,
        setCreateClientDeleted,
        restoreClient,
        proventosFileUrl,
        patrimoniosFileUrl,
        getClientsUpdating
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};

export const useClients = (): IClientsContext => useContext(ClientsContext);
